import { ROUTES } from '@/routes/routes';
import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiHome,
  FiUsers,
  FiSettings,
  FiKey,
  FiCreditCard,
  FiBarChart2,
  FiLogOut,
  FiChevronLeft,
  FiFileText,
} from 'react-icons/fi';
import { useAppDispatch, useAppSelector } from '@/store';
import { logout } from '@/store/slices/authSlice';
import { toggleSidebar } from '@/store/slices/uiSlice';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  {
    path: '/employer/dashboard',
    label: 'Dashboard',
    icon: <FiHome size={20} />,
  },
  {
    path: '/employer/candidates',
    label: 'Candidates',
    icon: <FiUsers size={20} />,
  },
  {
    path: '/employer/candidate-results',
    label: 'Results',
    icon: <FiBarChart2 size={20} />,
  },
  {
    path: '/employer/interview-config',
    label: 'Interview Config',
    icon: <FiFileText size={20} />,
  },
  { path: '/employer/api-keys', label: 'API Keys', icon: <FiKey size={20} /> },
  {
    path: '/employer/plan',
    label: 'Plan & Billing',
    icon: <FiCreditCard size={20} />,
  },
  {
    path: '/employer/settings',
    label: 'Settings',
    icon: <FiSettings size={20} />,
  },
];

const Sidebar: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sidebarOpen, screenSize } = useAppSelector(state => state.ui);
  const { user } = useAppSelector(state => state.auth);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate(ROUTES.login);
  };

  const isMobile = screenSize === 'mobile';

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {isMobile && sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => dispatch(toggleSidebar())}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          width: sidebarOpen ? (isMobile ? 280 : 260) : 0,
          opacity: sidebarOpen ? 1 : 0,
        }}
        transition={{ duration: 0.2 }}
        className={classNames(
          'fixed left-0 top-0 h-full bg-white border-r border-gray-200 z-50',
          'flex flex-col overflow-hidden',
          {
            'lg:relative': !isMobile,
          }
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-primary-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">IA</span>
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">Interview Agent</h1>
              <p className="text-xs text-gray-500">Employer Panel</p>
            </div>
          </div>
          <button
            onClick={() => dispatch(toggleSidebar())}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
          >
            <FiChevronLeft size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4">
          <ul className="space-y-1">
            {navItems.map(item => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    classNames(
                      'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                      {
                        'bg-blue-primary-500 text-white': isActive,
                        'text-gray-700 hover:bg-blue-primary-100 hover:text-blue-primary-500':
                          !isActive,
                      }
                    )
                  }
                  onClick={() => isMobile && dispatch(toggleSidebar())}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-primary-100 rounded-full flex items-center justify-center">
              <span className="text-blue-primary-500 font-medium">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email || 'user@example.com'}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-danger rounded-lg hover:bg-danger-100 transition-colors"
          >
            <FiLogOut size={20} />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </motion.aside>
    </>
  );
};

export default Sidebar;
